// src/screens/DonateScreen.jsx
import { useState, useMemo } from "react";
import { T, Btn, Label } from "../components/ui.jsx";

const GITHUB_DONATE = "https://github.com/sponsors/sir-zech";
const UPI_ID        = "sirzech@axl";
const PAYEE_NAME    = "Naveen";
const UPI_NOTE      = "Omerta tribute";
const PRESETS       = [99, 299, 499, 999];

function buildUpiUri(amount) {
  const params = new URLSearchParams({
    pa: UPI_ID,
    pn: PAYEE_NAME,
    cu: "INR",
    tn: UPI_NOTE,
  });
  if (amount && amount > 0) params.set("am", amount.toFixed(2));
  return `upi://pay?${params.toString()}`;
}

export default function DonateScreen({ onBack }) {
  const [tab,    setTab]    = useState("world");
  const [amount, setAmount] = useState(null);
  const [copied, setCopied] = useState(false);

  const upiUri = useMemo(() => buildUpiUri(amount), [amount]);
  const qrSrc  = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(upiUri)}&size=260x260&margin=10&qzone=2`,
    [upiUri]
  );

  function selectAmount(v) {
    setAmount((cur) => (cur === v ? null : v));
  }

  async function copyUpi() {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  }

  const tabBtnStyle = (active) => ({
    flex: 1,
    background: active ? "rgba(201,168,76,0.12)" : "transparent",
    color: active ? "var(--gold)" : "var(--paper2)",
    border: `1px solid ${active ? "rgba(201,168,76,0.45)" : "rgba(201,168,76,0.15)"}`,
    borderRadius: 2,
    padding: "0.7rem 0.8rem",
    fontFamily: "'Courier Prime', monospace",
    fontSize: "0.7rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "all 0.18s",
  });

  return (
    <div style={{ ...T.screenTop, padding: "2rem 1rem 4rem" }}>
      {/* Back link */}
      <div style={{ width: "100%", maxWidth: 560, marginBottom: "1.25rem" }}>
        <button
          onClick={onBack}
          style={{
            background: "transparent",
            border: "1px solid rgba(201,168,76,0.2)",
            borderRadius: 2,
            color: "var(--gold-dim)",
            padding: "0.4rem 0.85rem",
            fontFamily: "'Courier Prime', monospace",
            fontSize: "0.65rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          ← Back
        </button>
      </div>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <div style={{
          display: "inline-block",
          fontSize: "0.55rem",
          letterSpacing: "0.4em",
          color: "var(--gold-dim)",
          border: "1px solid rgba(201,168,76,0.25)",
          padding: "0.3rem 0.85rem",
          borderRadius: 2,
          marginBottom: "0.9rem",
        }}>
          PAY YOUR RESPECTS
        </div>
        <div style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "clamp(2rem, 6vw, 3rem)",
          fontWeight: 900,
          letterSpacing: "0.12em",
          color: "var(--paper)",
          marginBottom: "0.4rem",
        }}>
          A <span style={{ color: "var(--gold)" }}>Tribute</span> to the Family
        </div>
        <div style={{
          fontSize: "0.72rem",
          letterSpacing: "0.06em",
          color: "rgba(242,234,216,0.55)",
          lineHeight: 1.7,
          maxWidth: 480,
          margin: "0 auto",
        }}>
          Omertà runs on long nights and stronger coffee. No ads. No trackers.
          If the game has earned a place at your table, slip something under it.
        </div>
      </div>

      {/* Card */}
      <div style={{ ...T.card, maxWidth: 560, padding: 0, overflow: "hidden" }}>

        {/* Tabs */}
        <div style={{
          display: "flex",
          gap: "0.5rem",
          padding: "0.6rem",
          background: "rgba(0,0,0,0.25)",
          borderBottom: "1px solid rgba(201,168,76,0.15)",
        }}>
          <button style={tabBtnStyle(tab === "world")} onClick={() => setTab("world")}>
            ⚜ International
          </button>
          <button style={tabBtnStyle(tab === "india")} onClick={() => setTab("india")}>
            ☗ India · UPI
          </button>
        </div>

        {/* WORLD PANEL */}
        {tab === "world" && (
          <div style={{ padding: "1.75rem 1.5rem 2rem", animation: "fadeInUp 0.3s ease" }}>
            <div style={{ display: "flex", gap: "0.9rem", alignItems: "flex-start", marginBottom: "1.4rem" }}>
              <div style={{
                width: 42, height: 42, flexShrink: 0,
                background: "rgba(201,168,76,0.12)",
                border: "1px solid rgba(201,168,76,0.35)",
                color: "var(--gold)",
                borderRadius: 2,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.1rem",
              }}>
                ❦
              </div>
              <div>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.15rem",
                  color: "var(--gold)",
                  marginBottom: "0.25rem",
                  letterSpacing: "0.04em",
                }}>
                  GitHub Sponsors
                </div>
                <div style={{ fontSize: "0.78rem", color: "rgba(242,234,216,0.55)", lineHeight: 1.6 }}>
                  The cleanest way to deliver an envelope from anywhere on the map.
                  Settled by GitHub. No middlemen at the door.
                </div>
              </div>
            </div>

            <ul style={{
              listStyle: "none",
              background: "rgba(0,0,0,0.25)",
              border: "1px solid rgba(201,168,76,0.15)",
              borderRadius: 2,
              padding: "0.6rem 1rem",
              marginBottom: "1.5rem",
            }}>
              {[
                { icon: "✦", title: "Cards welcome", text: "Credit, debit, Apple Pay, Google Pay." },
                { icon: "⟳", title: "One time or monthly", text: "A single tribute, or a standing arrangement." },
                { icon: "⌬", title: "Sealed envelope", text: "GitHub handles the paperwork. Your card stays your card." },
              ].map((f, i, arr) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    gap: "0.8rem",
                    padding: "0.65rem 0",
                    borderBottom: i < arr.length - 1 ? "1px dashed rgba(201,168,76,0.12)" : "none",
                  }}
                >
                  <span style={{
                    width: 26, height: 26, flexShrink: 0,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(201,168,76,0.2)",
                    color: "var(--gold)",
                    borderRadius: 2,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.85rem",
                  }}>
                    {f.icon}
                  </span>
                  <div>
                    <div style={{ color: "var(--paper)", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.15rem", letterSpacing: "0.05em" }}>
                      {f.title}
                    </div>
                    <div style={{ color: "rgba(242,234,216,0.55)", fontSize: "0.74rem", lineHeight: 1.55 }}>
                      {f.text}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <a
              href={GITHUB_DONATE}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                textAlign: "center",
                background: "var(--gold)",
                color: "var(--ink)",
                fontFamily: "'Courier Prime', monospace",
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                padding: "0.9rem 1.2rem",
                borderRadius: 2,
                textDecoration: "none",
              }}
            >
              ❦ Open Sponsors Page ↗
            </a>
          </div>
        )}

        {/* INDIA PANEL */}
        {tab === "india" && (
          <div style={{ padding: "1.75rem 1.5rem 2rem", animation: "fadeInUp 0.3s ease" }}>
            <div style={{ display: "flex", gap: "0.9rem", alignItems: "flex-start", marginBottom: "1.4rem" }}>
              <div style={{
                width: 42, height: 42, flexShrink: 0,
                background: "rgba(201,168,76,0.12)",
                border: "1px solid rgba(201,168,76,0.35)",
                color: "var(--gold)",
                borderRadius: 2,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.1rem",
              }}>
                ☗
              </div>
              <div>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.15rem",
                  color: "var(--gold)",
                  marginBottom: "0.25rem",
                  letterSpacing: "0.04em",
                }}>
                  Pay by UPI
                </div>
                <div style={{ fontSize: "0.78rem", color: "rgba(242,234,216,0.55)", lineHeight: 1.6 }}>
                  GPay, PhonePe, Paytm, BHIM — any UPI app will do the job.
                </div>
              </div>
            </div>

            {/* QR */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.8rem", marginBottom: "1.5rem" }}>
              <div style={{
                position: "relative",
                background: "var(--paper)",
                padding: 12,
                borderRadius: 2,
                border: "1px solid rgba(201,168,76,0.3)",
                boxShadow: "0 0 28px rgba(201,168,76,0.1)",
              }}>
                <img src={qrSrc} alt="UPI QR code" width="240" height="240" style={{ display: "block" }} />
                <span style={{
                  position: "absolute",
                  top: -10, right: -10,
                  background: "var(--gold)",
                  color: "var(--ink)",
                  fontFamily: "'Courier Prime', monospace",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  padding: "4px 10px",
                  borderRadius: 2,
                  border: "1px solid var(--ink)",
                }}>
                  {amount ? `₹ ${amount}` : "ANY AMOUNT"}
                </span>
              </div>
              <div style={{ fontSize: "0.72rem", color: "rgba(242,234,216,0.5)", textAlign: "center", maxWidth: 320, lineHeight: 1.55 }}>
                Open any UPI app, choose <strong style={{ color: "var(--paper)" }}>Scan</strong>, point your camera.
              </div>
            </div>

            {/* Amounts */}
            <div style={{ marginBottom: "1.5rem" }}>
              <Label>Suggested Tribute</Label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem", marginTop: "0.5rem" }}>
                {PRESETS.map((p) => {
                  const active = amount === p;
                  return (
                    <button
                      key={p}
                      onClick={() => selectAmount(p)}
                      style={{
                        background: active ? "rgba(201,168,76,0.18)" : "rgba(255,255,255,0.03)",
                        border: `1px solid ${active ? "var(--gold)" : "rgba(201,168,76,0.2)"}`,
                        color: active ? "var(--gold)" : "var(--paper)",
                        fontFamily: "'Courier Prime', monospace",
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        padding: "0.55rem 1rem",
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      ₹ {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setAmount(null)}
                  style={{
                    background: amount === null ? "rgba(201,168,76,0.18)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${amount === null ? "var(--gold)" : "rgba(201,168,76,0.2)"}`,
                    color: amount === null ? "var(--gold)" : "var(--paper)",
                    fontFamily: "'Courier Prime', monospace",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    padding: "0.55rem 1rem",
                    borderRadius: 2,
                    cursor: "pointer",
                  }}
                >
                  YOUR CALL
                </button>
              </div>
              <div style={{ fontSize: "0.7rem", color: "rgba(242,234,216,0.4)", marginTop: "0.55rem", lineHeight: 1.5 }}>
                A chosen amount fills automatically when the QR is scanned.
              </div>
            </div>

            {/* UPI ID box */}
            <div style={{ marginBottom: "1rem" }}>
              <Label>Or send to this UPI ID</Label>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.6rem",
                background: "rgba(0,0,0,0.3)",
                border: "1px solid rgba(201,168,76,0.2)",
                borderRadius: 2,
                padding: "0.65rem 0.9rem",
                marginTop: "0.5rem",
              }}>
                <span style={{
                  fontFamily: "'Courier Prime', monospace",
                  fontSize: "0.92rem",
                  fontWeight: 700,
                  color: "var(--paper)",
                  letterSpacing: "0.04em",
                  wordBreak: "break-all",
                }}>
                  {UPI_ID}
                </span>
                <Btn
                  variant={copied ? "ghost" : "ghost"}
                  sm
                  full={false}
                  onClick={copyUpi}
                  style={{
                    color: copied ? "#4db8b8" : "var(--gold)",
                    borderColor: copied ? "#2a8080" : "rgba(201,168,76,0.35)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {copied ? "✓ Copied" : "⧉ Copy"}
                </Btn>
              </div>
              <div style={{ fontSize: "0.7rem", color: "rgba(242,234,216,0.4)", marginTop: "0.55rem", lineHeight: 1.5 }}>
                Paste into the "Pay to UPI ID" field if scanning is not an option.
              </div>
            </div>

            <a
              href={upiUri}
              style={{
                display: "block",
                textAlign: "center",
                background: "transparent",
                color: "var(--gold)",
                border: "1px solid rgba(201,168,76,0.35)",
                fontFamily: "'Courier Prime', monospace",
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                padding: "0.85rem 1.2rem",
                borderRadius: 2,
                textDecoration: "none",
                marginTop: "0.5rem",
              }}
            >
              ☗ Open Payment App
            </a>
          </div>
        )}
      </div>

      {/* Thanks */}
      <div style={{ textAlign: "center", marginTop: "2.25rem", maxWidth: 480 }}>
        <div style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "1rem",
          fontStyle: "italic",
          color: "var(--paper)",
          marginBottom: "0.4rem",
          letterSpacing: "0.03em",
        }}>
          "Grazie. The Family does not forget."
        </div>
        <div style={{ fontSize: "0.72rem", color: "rgba(242,234,216,0.45)", lineHeight: 1.65 }}>
          Every tribute, big or small, keeps the lights on and the silence sacred.
        </div>
      </div>
    </div>
  );
}
